import type { AchievementDef, AchievementSummary, ConfigData } from '../../../types';
import { StateManager } from '../../../managers';
import { escapeHtml } from './types';

type CategoryFilter = '' | 'story' | 'combat' | 'crafting' | 'collection' | 'meta';

interface AchievementsState {
  categoryFilter: CategoryFilter;
}

const CATEGORY_LABELS: Record<Exclude<CategoryFilter, ''> | 'all', string> = {
  all: 'All',
  story: 'Story',
  combat: 'Combat',
  crafting: 'Crafting',
  collection: 'Collection',
  meta: 'Meta',
};

const ORDER: Exclude<CategoryFilter, ''>[] = ['story', 'combat', 'crafting', 'collection', 'meta'];

const state: AchievementsState = { categoryFilter: '' };

function getCatalog(): AchievementDef[] {
  const config = StateManager.get('configData') as ConfigData | null;
  return config?.achievements ?? [];
}

function getSummary(): AchievementSummary | null {
  return StateManager.get('achievementData') as AchievementSummary | null;
}

export function renderAchievements(content: HTMLElement): void {
  const catalog = getCatalog();
  const summary = getSummary();
  const unlocked = new Set(summary?.unlocked ?? []);
  const counters = summary?.counters ?? {};

  if (catalog.length === 0) {
    content.innerHTML = '<p>Loading achievements...</p>';
    return;
  }

  const counts: Record<string, number> = { all: catalog.length };
  for (const cat of ORDER) counts[cat] = catalog.filter(a => a.category === cat).length;
  const unlockedTotal = catalog.filter(a => unlocked.has(a.id)).length;

  const filtered = state.categoryFilter
    ? catalog.filter(a => a.category === state.categoryFilter)
    : catalog;

  const pillRow = (['all', ...ORDER] as const).map(c => {
    const active = (state.categoryFilter === '' && c === 'all') || state.categoryFilter === c;
    return `<button class="codex-pill${active ? ' active' : ''}" data-ach-cat="${c}">${CATEGORY_LABELS[c]} <span class="codex-pill-count">${counts[c]}</span></button>`;
  }).join('');

  const cardsHtml = filtered.map(a => cardHtml(a, unlocked.has(a.id), counters[a.id] ?? 0)).join('');

  content.innerHTML = `
    <div class="codex-achievements">
      <div class="codex-achievements-summary">
        <span class="codex-achievements-progress">${unlockedTotal} / ${catalog.length} unlocked</span>
      </div>
      <div class="codex-pill-row">${pillRow}</div>
      <div class="codex-achievements-grid">${cardsHtml}</div>
    </div>
  `;

  content.querySelectorAll('[data-ach-cat]').forEach(btn => {
    btn.addEventListener('click', () => {
      const cat = (btn as HTMLElement).dataset.achCat as CategoryFilter | 'all';
      state.categoryFilter = cat === 'all' ? '' : cat;
      renderAchievements(content);
    });
  });
}

function cardHtml(def: AchievementDef, isUnlocked: boolean, counter: number): string {
  const hiddenLocked = def.hidden && !isUnlocked;
  const name = hiddenLocked ? '???' : escapeHtml(def.name);
  const description = hiddenLocked ? 'Hidden — unlock to reveal.' : escapeHtml(def.description);
  const lockClass = isUnlocked ? 'unlocked' : 'locked';

  let progressHtml = '';
  if (!isUnlocked && def.threshold != null && !def.hidden) {
    const value = Math.min(counter, def.threshold);
    const pct = Math.round((value / def.threshold) * 100);
    progressHtml = `
      <div class="codex-achievement-progress">
        <div class="codex-achievement-progress-bar"><div style="width: ${pct}%"></div></div>
        <div class="codex-achievement-progress-text">${value} / ${def.threshold}</div>
      </div>
    `;
  }

  return `
    <div class="codex-achievement-card codex-achievement-card--${lockClass}">
      <div class="codex-achievement-icon">${isUnlocked ? '🏆' : (hiddenLocked ? '❔' : '🔒')}</div>
      <div class="codex-achievement-body">
        <div class="codex-achievement-name">${name}</div>
        <div class="codex-achievement-desc">${description}</div>
        ${progressHtml}
      </div>
    </div>
  `;
}
